<template>
  <section
    class="relative overflow-hidden rounded-2xl border border-primary/15 bg-gradient-to-br from-primary/[0.08] via-primary/[0.03] to-background"
    aria-labelledby="workspace-context-title"
  >
    <!-- Soft brand glow behind the logo -->
    <div
      class="pointer-events-none absolute -left-16 -top-16 size-56 rounded-full bg-primary/10 blur-3xl"
      aria-hidden="true"
    />

    <div
      v-if="loading"
      class="relative flex items-center gap-5 p-6"
      aria-busy="true"
    >
      <Skeleton class="size-20 shrink-0 rounded-2xl" />
      <div class="flex-1 space-y-2">
        <Skeleton class="h-8 w-56" />
        <Skeleton class="h-4 w-72" />
        <Skeleton class="h-4 w-56" />
      </div>
      <Skeleton class="hidden h-10 w-48 rounded-lg md:block" />
    </div>

    <div
      v-else
      class="relative flex flex-wrap items-center gap-x-6 gap-y-4 p-6"
    >
      <div class="flex min-w-0 flex-1 basis-80 items-center gap-5">
        <WorkspaceLogo
          :workspace="workspace"
          size="lg"
          class="ring-4 ring-background"
        />

        <div class="min-w-0 flex-1">
          <div class="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
            <h2
              id="workspace-context-title"
              class="truncate text-2xl font-semibold tracking-tight text-foreground sm:text-3xl"
              :title="workspace?.name"
            >
              {{ workspace?.name || 'Workspace' }}
            </h2>
            <StatusBadge
              v-if="workspace?.status"
              :status="workspace.status"
              class="capitalize"
            />
          </div>

          <div class="mt-2 flex min-w-0 flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
            <span
              v-if="workspace?.workspace_number"
              class="inline-flex items-center gap-1.5 font-mono text-primary"
            >
              <Hash
                class="size-3.5"
                aria-hidden="true"
              />
              {{ workspace.workspace_number }}
            </span>
            <span
              v-if="showLegalName"
              class="inline-flex min-w-0 items-center gap-1.5"
            >
              <Building2
                class="size-3.5 shrink-0"
                aria-hidden="true"
              />
              <span class="truncate">{{ workspace.legal_name }}</span>
            </span>
            <a
              v-if="websiteHref"
              :href="websiteHref"
              target="_blank"
              rel="noopener noreferrer"
              class="inline-flex min-w-0 max-w-[16rem] items-center gap-1.5 text-primary hover:underline"
              :title="websiteHref"
            >
              <Globe
                class="size-3.5 shrink-0"
                aria-hidden="true"
              />
              <span class="truncate">{{ websiteLabel }}</span>
            </a>
          </div>

          <p
            v-if="descriptionPlain"
            class="mt-2 line-clamp-2 max-w-3xl text-sm text-muted-foreground"
            :title="descriptionPlain"
          >
            {{ descriptionPlain }}
          </p>
        </div>
      </div>

      <div class="flex shrink-0 flex-wrap items-center gap-2">
        <div
          v-if="workspace?.cco"
          class="flex max-w-[11rem] min-w-0 items-center gap-2 rounded-lg border bg-background/80 py-1 pe-2.5 ps-1 shadow-sm backdrop-blur"
          :title="ccoName"
        >
          <CAvatar
            :user="workspace.cco"
            avatar-class="size-7 text-[10px]"
          />
          <div class="min-w-0 leading-tight">
            <p class="truncate text-xs font-medium text-foreground">
              {{ ccoName }}
            </p>
            <p class="truncate text-[10px] text-muted-foreground">
              CCO
            </p>
          </div>
        </div>

        <div
          v-else
          class="flex items-center gap-2 rounded-lg border border-dashed bg-background/80 px-2.5 py-1.5 backdrop-blur"
        >
          <span class="flex size-7 items-center justify-center rounded-full bg-muted text-muted-foreground">
            <UserRoundPlus
              class="size-3.5"
              aria-hidden="true"
            />
          </span>
          <div class="text-xs leading-tight">
            <p class="font-medium text-foreground">
              No CCO
            </p>
          </div>
          <Button
            v-if="canEdit"
            size="xs"
            @click="actions?.showUserDialog?.()"
          >
            Assign
          </Button>
        </div>

        <div
          v-if="canEdit"
          class="flex items-center gap-1"
        >
          <Button
            size="icon"
            variant="ghost"
            class="size-8 text-muted-foreground"
            title="Update description"
            aria-label="Update description"
            @click="actions?.openDescriptionDialog?.()"
          >
            <Pencil class="size-4" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            class="size-8 text-muted-foreground"
            title="Workspace settings"
            aria-label="Workspace settings"
            @click="actions?.redirectToSettings?.()"
          >
            <Settings class="size-4" />
          </Button>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, inject } from 'vue'
import { storeToRefs } from 'pinia'
import { Building2, Globe, Hash, Pencil, Settings, UserRoundPlus } from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import CAvatar from '@/components/common/CAvatar.vue'
import StatusBadge from '@/components/common/StatusBadge.vue'
import WorkspaceLogo from '@/components/workspaces/WorkspaceLogo.vue'
import { useAuthStore, useWorkspaceStore } from '@/stores'

defineOptions({ name: 'WorkspaceContextCard' })

type WorkspaceHeaderActions = {
  openDescriptionDialog?: () => void
  showUserDialog?: () => void
  redirectToSettings?: () => void
}

const actions = inject<WorkspaceHeaderActions | null>('workspaceHeaderActions', null)

const authStore = useAuthStore()
const workspaceStore = useWorkspaceStore()
const { selectedWorkspace, loadingWorkspaceByID } = storeToRefs(workspaceStore)

const workspace = computed(() => selectedWorkspace.value)
const loading = computed(() => loadingWorkspaceByID.value && !selectedWorkspace.value)
const canEdit = computed(() => authStore.canEditShareable)

const descriptionPlain = computed(() =>
  String(workspace.value?.description ?? '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim(),
)

const showLegalName = computed(() =>
  Boolean(workspace.value?.legal_name) && workspace.value?.legal_name !== workspace.value?.name,
)

/** Prefer `website`; fall back to common alternate keys some payloads still send. */
const websiteRaw = computed(() => {
  const ws = workspace.value as Record<string, unknown> | null | undefined
  if (!ws) return ''
  const candidates = [ws.website, ws.company_website, ws.web_url, ws.url]
  for (const value of candidates) {
    const raw = String(value ?? '').trim()
    if (raw) return raw
  }
  return ''
})

const websiteHref = computed(() => {
  const raw = websiteRaw.value
  if (!raw) return null
  return /^https?:\/\//i.test(raw) ? raw : `https://${raw}`
})

const websiteLabel = computed(() =>
  websiteHref.value?.replace(/^https?:\/\/(www\.)?/i, '').replace(/\/$/, '') ?? '',
)

const ccoName = computed(() =>
  workspace.value?.cco?.full_name || workspace.value?.cco?.name || 'Compliance officer',
)
</script>
